import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  CheckCircle, AlertTriangle, Circle, FileText, Shield,
  Briefcase, Search, ChevronDown, ChevronUp, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ICON = {
  complete: <CheckCircle size={15} className="text-emerald-400 shrink-0" />,
  incomplete: <AlertTriangle size={15} className="text-amber-400 shrink-0" />,
  missing: <Circle size={15} className="text-slate-600 shrink-0" />,
  pending: <Circle size={15} className="text-blue-400 shrink-0" />,
  pending_verification: <Circle size={15} className="text-blue-400 shrink-0" />,
};

const SECTION_ICON = {
  profile: <FileText size={16} />,
  representatives: <Briefcase size={16} />,
  documents: <FileText size={16} />,
  screening: <Search size={16} />,
};

function ProgressRing({ percentage, size = 48 }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percentage / 100) * circ;
  const color = percentage === 100 ? "#34d399" : percentage >= 60 ? "#fbbf24" : "#f87171";
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1e293b" strokeWidth="4" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4"
        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`} className="transition-all duration-700" />
      <text x="50%" y="50%" textAnchor="middle" dy=".35em" fill={color} fontSize="12" fontWeight="700">
        {percentage}%
      </text>
    </svg>
  );
}

function SectionCard({ sectionKey, section, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const isComplete = section.status === "complete";
  const isPending = section.status === "pending" || section.status === "pending_verification";
  const borderColor = isComplete ? "border-emerald-500/20" : isPending ? "border-blue-500/20" : "border-amber-500/20";
  const bgColor = isComplete ? "bg-emerald-500/3" : isPending ? "bg-blue-500/3" : "bg-amber-500/3";

  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} overflow-hidden`} data-testid={`readiness-section-${sectionKey}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-800/20 transition-colors"
      >
        <span className={isComplete ? "text-emerald-400" : isPending ? "text-blue-400" : "text-amber-400"}>
          {SECTION_ICON[sectionKey] || <Circle size={16} />}
        </span>
        <span className="flex-1 text-sm font-medium text-slate-200">{section.label}</span>
        {section.percentage !== undefined && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            section.percentage === 100 ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
          }`}>{section.percentage}%</span>
        )}
        {section.count !== undefined && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            section.count >= (section.required_minimum || 1) ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
          }`}>{section.count}/{section.required_minimum || 1}</span>
        )}
        {STATUS_ICON[section.status]}
        {open ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
      </button>

      {open && (
        <div className="px-4 pb-3 space-y-1.5">
          {/* Profile items */}
          {section.items?.map((item) => (
            <div key={item.field || item.doc_type || item.representative_id || item.name}
              className="flex items-center gap-2 text-sm py-1 px-2 rounded"
            >
              {item.status === "complete" || item.uploaded === true || item.verification_status === "verified"
                ? STATUS_ICON.complete
                : item.status === "missing" || item.uploaded === false
                  ? (item.required !== false ? STATUS_ICON.incomplete : STATUS_ICON.missing)
                  : STATUS_ICON.pending
              }
              <span className={`flex-1 ${
                (item.status === "complete" || item.uploaded === true) ? "text-slate-300" : "text-slate-500"
              }`}>
                {item.label || item.name || item.full_name}
                {item.required === false && <span className="text-xs text-slate-600 ml-1">(opcional)</span>}
              </span>
              {/* Rep-specific details */}
              {item.representation_type && (
                <span className="text-xs text-slate-500">
                  {item.representation_type === "power_of_attorney" ? "Poder" : "Asamblea"}
                </span>
              )}
              {item.verification_status && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  item.verification_status === "verified" ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-700 text-slate-400"
                }`}>{item.verification_status === "verified" ? "Verificado" : "Pendiente"}</span>
              )}
              {item.is_pep && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400">PEP</span>
              )}
              {item.has_rfc === false && item.representative_id && (
                <span className="text-xs text-red-400">Sin RFC</span>
              )}
            </div>
          ))}

          {/* Screening-specific */}
          {sectionKey === "screening" && (
            <>
              {/* If screening is pending, show action message */}
              {(section.status === "pending" || section.status === "pending_verification") && !section.customer_screened && (
                <div className="flex items-center gap-2 text-sm py-2 px-2 bg-blue-500/10 border border-blue-500/20 rounded">
                  <Search size={14} className="text-blue-400" />
                  <span className="text-blue-300">
                    Haga clic en <strong>"Ejecutar Screening PEP/OFAC"</strong> en los botones de acción arriba
                  </span>
                </div>
              )}
              
              {/* If screening has unresolved matches */}
              {section.status === "incomplete" && section.customer_screened && (
                <div className="flex items-center gap-2 text-sm py-2 px-2 bg-amber-500/10 border border-amber-500/20 rounded mb-2">
                  <AlertTriangle size={14} className="text-amber-400" />
                  <span className="text-amber-300">
                    Resuelva las coincidencias pendientes antes de verificar el KYC
                  </span>
                </div>
              )}
              
              {/* If screening has been performed, show results */}
              {section.customer_screened && (
                <>
                  <div className="flex items-center gap-2 text-sm py-1 px-2">
                    {STATUS_ICON.complete}
                    <span className="text-slate-300">Screening del cliente</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      section.customer_result === "clear" ? "bg-emerald-500/10 text-emerald-400" : 
                      section.customer_result === "critical" ? "bg-red-500/10 text-red-400" :
                      section.customer_result === "high" ? "bg-orange-500/10 text-orange-400" :
                      "bg-amber-500/10 text-amber-400"
                    }`}>
                      {section.customer_result === "clear" ? "Sin coincidencias" : 
                       section.customer_result === "critical" ? "Crítico" :
                       section.customer_result === "high" ? "Alto Riesgo" : "Revisar"}
                    </span>
                  </div>
                  
                  {/* Show matches details if screening was performed */}
                  {section.total_matches > 0 && (
                    <div className="ml-6 space-y-1">
                      <div className="flex items-center gap-2 text-xs py-0.5 px-2 text-slate-400">
                        <AlertTriangle size={12} className="text-amber-400" />
                        <span>{section.active_matches} coincidencia(s) activa(s)</span>
                      </div>
                      {section.dismissed_matches > 0 && (
                        <div className="flex items-center gap-2 text-xs py-0.5 px-2 text-slate-500">
                          <CheckCircle size={12} className="text-slate-500" />
                          <span>{section.dismissed_matches} descartada(s)</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {section.total_matches === 0 && (
                    <div className="ml-6 text-xs py-0.5 px-2 text-emerald-400">
                      ✓ Sin coincidencias en listas PEP/OFAC
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ComplianceReadiness({ customerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReadiness = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customers/${customerId}/compliance-readiness`, { withCredentials: true });
      setData(res.data);
    } catch {
      // silent — endpoint may not exist for non-moral
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { fetchReadiness(); }, [fetchReadiness]);

  if (loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 flex items-center justify-center h-32">
        <Loader2 size={20} className="text-teal-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { overall_ready, overall_percentage, sections, blockers, kyc_status } = data;
  const isVerified = kyc_status === "verified";

  return (
    <div className="space-y-4" data-testid="compliance-readiness">
      {/* Overall status card */}
      <div className={`rounded-xl border p-5 ${
        isVerified ? "bg-emerald-500/5 border-emerald-500/20" :
        overall_ready ? "bg-teal-500/5 border-teal-500/20" :
        "bg-slate-900 border-slate-800"
      }`}>
        <div className="flex items-center gap-5">
          <ProgressRing percentage={isVerified ? 100 : overall_percentage} size={56} />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-white flex items-center gap-2" data-testid="readiness-title">
              <Shield size={18} className="text-teal-400" />
              {isVerified ? "KYC Verificado" : "Preparacion para Verificacion KYC"}
            </h2>
            <p className="text-sm text-slate-400 mt-0.5">
              {isVerified
                ? "Todos los requisitos de cumplimiento han sido satisfechos."
                : overall_ready
                  ? "Todos los requisitos estan completos. Puede proceder a verificar el KYC."
                  : `${blockers.length} requisito(s) pendiente(s) para completar la verificacion.`
              }
            </p>
          </div>
          {isVerified && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <CheckCircle size={16} className="text-emerald-400" />
              <span className="text-sm font-medium text-emerald-400">Verificado</span>
            </div>
          )}
          {!isVerified && overall_ready && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20">
              <CheckCircle size={16} className="text-teal-400" />
              <span className="text-sm font-medium text-teal-400">Listo</span>
            </div>
          )}
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {Object.entries(sections).map(([key, section]) => (
          <SectionCard key={key} sectionKey={key} section={section}
            defaultOpen={section.status !== "complete" && !isVerified} />
        ))}
      </div>

      {/* Blockers */}
      {!isVerified && blockers.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/15 rounded-xl px-5 py-4" data-testid="readiness-blockers">
          <h3 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
            <AlertTriangle size={14} /> Requisitos Pendientes ({blockers.length})
          </h3>
          <ul className="space-y-1">
            {blockers.map((b, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                <Circle size={6} className="text-red-400/50 shrink-0" fill="currentColor" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
